import axios from 'axios';
import { ENDPOINTS } from '../constants/endpoints';
import { AuthService } from './auth.service';
import { saveBlob } from '../utils/saveBlob';
import { parseContentDispositionFilename } from '../utils/contentDisposition';

// ---- W3-K journal import / Tally export ----

export interface JournalImportRowResult { rowNumber: number; entryRef: string; valid: boolean; errors: string[]; }
export interface JournalImportEntryPreview {
    entryRef: string; date: string | null; lineCount: number; totalDebit: number; totalCredit: number; valid: boolean; errors: string[];
}
export interface JournalImportReport {
    totalRows: number; entryCount: number; validEntries: number; errorRows: number;
    batchErrors: string[]; entries: JournalImportEntryPreview[]; rows: JournalImportRowResult[];
}
export interface JournalImportCommitResult {
    batchId: string; entryCount: number; lineCount: number;
    entries: { entryRef: string; journalEntryId: string; entryNumber: string }[];
}
export interface JournalImportBatch {
    id: string; fileName: string | null; status: 'COMMITTED' | 'DELETED'; entryCount: number; lineCount: number;
    createdAt: string | null; draftCount: number; postedCount: number; voidedCount: number;
}
export interface TallyLedgerMapRow {
    accountId: string; accountCode: string; accountName: string; defaultGroup: string;
    tallyGroupName: string | null; tallyLedgerName: string | null;
}

const institution = () => AuthService.getSelectedInstitution()!;

const upload = async (url: string, file: File, params?: Record<string, string>) => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await axios.post(url, formData, { params, headers: { 'Content-Type': 'multipart/form-data' } });
    return response.data;
};

export const JOURNAL_IMPORT_TEMPLATE = {
    headers: ['entry_ref', 'date', 'account_code', 'debit', 'credit', 'description', 'segment_code', 'department_code', 'project_code'], // department_code: B4-H, project_code: P1-A, both optional
    rows: [
        ['JV-001', '2026-04-01', '5002010100', '1000.00', '', 'Basic salary paid', '', ''],
        ['JV-001', '2026-04-01', '1001010101', '', '1000.00', 'Paid in cash (Cash in Hand - BDT)', '', ''],
    ] as (string | number)[][],
};

export const JournalImportService = {
    dryRun: async (file: File): Promise<JournalImportReport> => upload(ENDPOINTS.JOURNAL_IMPORT.DRY_RUN(institution()), file),
    commit: async (file: File, allowDuplicate = false): Promise<JournalImportCommitResult> =>
        upload(ENDPOINTS.JOURNAL_IMPORT.COMMIT(institution()), file, allowDuplicate ? { allowDuplicate: 'true' } : undefined),
    listBatches: async (): Promise<JournalImportBatch[]> => (await axios.get(ENDPOINTS.JOURNAL_IMPORT.BATCHES(institution()))).data,
    deleteBatch: async (batchId: string): Promise<void> => { await axios.delete(ENDPOINTS.JOURNAL_IMPORT.BATCH(batchId)); },
};

const downloadXml = async (url: string, params: Record<string, string>, fallback: string) => {
    const response = await axios.get(url, { params, responseType: 'blob' });
    const disposition: string = response.headers?.['content-disposition'] ?? '';
    saveBlob(new Blob([response.data], { type: 'application/xml' }), parseContentDispositionFilename(disposition, fallback));
};

export const TallyExportService = {
    downloadMasters: () => downloadXml(ENDPOINTS.TALLY_EXPORT.MASTERS(institution()), {}, 'tally-masters.xml'),
    downloadVouchers: (from: string, to: string) =>
        downloadXml(ENDPOINTS.TALLY_EXPORT.VOUCHERS(institution()), { from, to }, `tally-vouchers-${from}-to-${to}.xml`),
    getLedgerMap: async (): Promise<TallyLedgerMapRow[]> => (await axios.get(ENDPOINTS.TALLY_EXPORT.LEDGER_MAP(institution()))).data,
    saveOverride: async (accountId: string, tallyGroupName: string, tallyLedgerName: string): Promise<TallyLedgerMapRow> =>
        (await axios.put(ENDPOINTS.TALLY_EXPORT.LEDGER_MAP(institution()), {
            accountId, tallyGroupName, tallyLedgerName: tallyLedgerName || null,
        })).data,
    removeOverride: async (accountId: string): Promise<void> => {
        await axios.delete(ENDPOINTS.TALLY_EXPORT.LEDGER_MAP_ONE(institution(), accountId));
    },
};
// ---- end W3-K journal import / Tally export ----
